import React, {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import Snapshot, { type SnapshotOptions } from '@tiptap-pro/extension-snapshot'
import { TiptapCollabProvider, WebSocketStatus } from '@tiptap-pro/provider'
import { generateHTML } from '@tiptap/core'
import { Editor, type JSONContent } from '@tiptap/core'
import type { AnyExtension, FocusPosition } from '@tiptap/core'
import { Editor as TiptapEditor } from '@tiptap/core'
import Blockquote, {
  type BlockquoteOptions,
} from '@tiptap/extension-blockquote'
import Bold, { type BoldOptions } from '@tiptap/extension-bold'
import BubbleMenuExtension, {
  type BubbleMenuOptions,
} from '@tiptap/extension-bubble-menu'
import Code from '@tiptap/extension-code'
import CodeBlock, { type CodeBlockOptions } from '@tiptap/extension-code-block'
import Collaboration, {
  type CollaborationOptions,
} from '@tiptap/extension-collaboration'
import CollaborationCaret, {
  type CollaborationCaretOptions,
} from '@tiptap/extension-collaboration-caret'
import Document from '@tiptap/extension-document'
import HardBreak, { type HardBreakOptions } from '@tiptap/extension-hard-break'
import Heading, { type HeadingOptions } from '@tiptap/extension-heading'
import Highlight, { type HighlightOptions } from '@tiptap/extension-highlight'
import HorizontalRule, {
  type HorizontalRuleOptions,
} from '@tiptap/extension-horizontal-rule'
import Italic, { type ItalicOptions } from '@tiptap/extension-italic'
import { type LinkOptions } from '@tiptap/extension-link'
import {
  BulletList,
  type BulletListOptions,
  ListItem,
  type ListItemOptions,
  OrderedList,
  type OrderedListOptions,
} from '@tiptap/extension-list'
import Paragraph, { type ParagraphOptions } from '@tiptap/extension-paragraph'
import Strike, { type StrikeOptions } from '@tiptap/extension-strike'
import Subscript from '@tiptap/extension-subscript'
import Superscript from '@tiptap/extension-superscript'
import {
  Table,
  TableCell,
  type TableCellOptions,
  TableHeader,
  type TableHeaderOptions,
  type TableOptions,
  TableRow,
  type TableRowOptions,
} from '@tiptap/extension-table'
import Text from '@tiptap/extension-text'
import TextAlign, { type TextAlignOptions } from '@tiptap/extension-text-align'
import Typography, {
  type TypographyOptions,
} from '@tiptap/extension-typography'
import Underline, { type UnderlineOptions } from '@tiptap/extension-underline'
import {
  Gapcursor,
  Placeholder,
  type PlaceholderOptions as PlaceholderOptionsBase,
  UndoRedo,
  type UndoRedoOptions,
} from '@tiptap/extensions'
import type { EditorOptions } from '@tiptap/react'
import {
  Editor as TiptapReactEditor,
  useEditor,
  useEditorState,
} from '@tiptap/react'
import { renderToReactElement } from '@tiptap/static-renderer/pm/react'
import randomcolor from 'randomcolor'
import { twMerge } from 'tailwind-merge'
import { v4 as uuidv4 } from 'uuid'
import * as Y from 'yjs'

import { RICH_TEXT_AREA_NO_BLUR_CLASSNAME } from '@/components/common/TextArea/RichTextArea/constants'
import { useSetVersions } from '@/components/common/TextArea/RichTextArea/context'
import {
  isShiftEnterMessage,
  jsonToMarkdown,
  markdownToHtml,
} from '@/components/common/TextArea/RichTextArea/lib'
import type {
  CollaborationConfig,
  RichTextAreaProps,
  RichTextAreaRef,
} from '@/components/common/TextArea/RichTextArea/types'

import { TIPTAP_URL } from '@/constants/env'

import { Indent } from '@/extensions/Autocomplete/Tab'
import {
  ChatCitation,
  type ChatCitationOptions,
} from '@/extensions/ChatCitation'
import {
  Comment,
  type CommentOptions,
  HiddenComment,
} from '@/extensions/Comment'
import { ControlClickLink } from '@/extensions/ControlClickLink'
import {
  Mention,
  type MentionArgs,
  type Suggestions,
  type MentionExtensionOptions as TiptapMentionOptions,
  useSuggestions,
} from '@/extensions/Mention'
import { RequireHeading } from '@/extensions/RequireHeading'
import { Selection } from '@/extensions/Selection'
import {
  TextStyleExtended,
  type TextStyleOptions,
} from '@/extensions/TextStyle'

import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

interface NoOptions {}

interface PlaceholderOptions extends Partial<PlaceholderOptionsBase> {
  placeholder: PlaceholderOptionsBase['placeholder']
  className?: string
}

export interface ExtensionsConfig {
  blockquote?: BlockquoteOptions
  bold?: BoldOptions
  bubbleMenu?: BubbleMenuOptions
  bulletList?: BulletListOptions
  codeBlock?: CodeBlockOptions
  collabHistory?: SnapshotOptions
  collaboration?: CollaborationOptions
  collaborationCursor?: CollaborationCaretOptions
  comment?: Partial<CommentOptions> | false
  document?: NoOptions
  gapCursor?: NoOptions
  hardBreak?: HardBreakOptions
  heading?: HeadingOptions
  highlight?: HighlightOptions
  chatCitation?: Partial<ChatCitationOptions>
  history?: UndoRedoOptions
  horizontalRule?: HorizontalRuleOptions
  indent?: NoOptions | false
  italic?: ItalicOptions
  link?: LinkOptions
  listItem?: ListItemOptions
  mention?: MentionArgs
  orderedList?: OrderedListOptions
  paragraph?: ParagraphOptions
  placeholder?: Partial<PlaceholderOptions>
  requireHeading?: boolean
  selection?: NoOptions
  strike?: StrikeOptions
  subscript?: NoOptions
  superscript?: NoOptions
  table?: TableOptions
  tableCell?: TableCellOptions
  tableHeader?: TableHeaderOptions
  tableRow?: TableRowOptions
  text?: NoOptions
  textAlign?: TextAlignOptions
  textStyle?: TextStyleOptions
  typography?: TypographyOptions
  underline?: UnderlineOptions
}

interface UseRichTextAreaProps extends Omit<
  RichTextAreaProps,
  'collaborationId'
> {
  ref: React.ForwardedRef<RichTextAreaRef>
  collaborationConfig?: CollaborationConfig
  setIsFocused: (isFocused: boolean) => void
}

interface UseRichTextAreaHandlersProps extends Omit<
  UseRichTextAreaProps,
  'collaborationConfig'
> {
  editor: TiptapReactEditor | null
}

interface UseExtensionsOptions {
  collaborationConfig?: CollaborationConfig
  extensionOptions?: ExtensionsConfig
  onSynced?: () => void
  onStatus?: (status: WebSocketStatus) => void
}

interface UseRichTextEditorOptions extends Partial<EditorOptions> {
  collaborationConfig?: CollaborationConfig
  extensionOptions?: ExtensionsConfig
  onSynced?: () => void
  onStatus?: (status: WebSocketStatus) => void
}

const getJsonContent = (json: JSONContent) => {
  if ('default' in json) {
    return json.default
  }
  return json
}

const getJsonContentFromString = (json: string) => {
  let parsed: JSONContent
  try {
    parsed = JSON.parse(json)
  } catch {
    parsed = {
      type: 'doc',
      content: [
        {
          type: 'paragraph',
          content: [{ type: 'text', text: json }],
        },
      ],
    }
  }
  if ('default' in parsed) {
    return parsed.default
  }
  return parsed
}

const convertMentionArgsToTiptapMentionOptions = (
  suggestion: Suggestions | undefined,
  mentionArgs?: MentionArgs,
): Partial<TiptapMentionOptions> | undefined => {
  if (!mentionArgs) {
    return undefined
  }
  let className = ''
  if (mentionArgs.className) {
    className = mentionArgs.className
  } else if (mentionArgs.suggestion.type === 'knowledge-base') {
    className =
      'font-medium text-blue-600 america:text-red-600 dark:text-blue-200 rounded-[3.5px] px-[3px] py-[1px] bg-blue-100 america:bg-red-100 dark:bg-blue-600/50'
  } else if (mentionArgs.suggestion.type === 'user') {
    className =
      'font-normal text-blue-600 america:text-red-600 dark:text-blue-400'
  }
  return {
    suggestion,
    HTMLAttributes: { class: className },
  }
}

interface GetBaseExtensionsArgs extends Omit<ExtensionsConfig, 'mention'> {
  mention?: Partial<TiptapMentionOptions>
}

export const getBaseExtensions = (extensionOptions?: GetBaseExtensionsArgs) => {
  let extensions: AnyExtension[] = [
    Blockquote.configure({
      ...extensionOptions?.blockquote,
    }),
    Bold.configure({
      ...extensionOptions?.bold,
    }),
    BubbleMenuExtension.configure({
      ...extensionOptions?.bubbleMenu,
    }),
    BulletList.configure({
      HTMLAttributes: {
        class: 'list-disc marker:text-primary-900 dark:marker:text-primary-100',
      },
      ...extensionOptions?.bulletList,
    }),
    Code,
    CodeBlock.configure({
      ...extensionOptions?.codeBlock,
    }),
    ControlClickLink.configure({
      HTMLAttributes: {
        class:
          'font-normal text-blue-600 dark:text-blue-400 hover:underline underline-offset-2',
      },
      ...extensionOptions?.link,
    }),
    HardBreak.configure({
      ...extensionOptions?.hardBreak,
    }),
    Heading.configure({
      levels: [1, 2, 3, 4, 5, 6],
      ...extensionOptions?.heading,
    }),
    Highlight.configure({
      HTMLAttributes: {
        class:
          'bg-red-200 dark:bg-red-700/60 border-b border-dashed dark:text-inherit border-red-400 dark:border-red-500 py-0',
      },
      ...extensionOptions?.highlight,
    }),
    HorizontalRule.configure({
      ...extensionOptions?.horizontalRule,
    }),
    Italic.configure({
      ...extensionOptions?.italic,
    }),
    ListItem.configure({
      HTMLAttributes: {
        class: 'm-0',
      },
      ...extensionOptions?.listItem,
    }),
    OrderedList.configure({
      HTMLAttributes: {
        class:
          'list-decimal marker:text-primary-900 dark:marker:text-primary-100',
      },
      ...extensionOptions?.orderedList,
    }),
    Paragraph,
    Placeholder.configure({
      placeholder: extensionOptions?.placeholder?.placeholder ?? '',
      showOnlyWhenEditable: false,
      showOnlyCurrent: true,
      // first child of the editor
      emptyNodeClass: twMerge(
        'cursor-text first:before:content-[attr(data-placeholder)] before:text-primary-300 dark:before:text-primary-500 before:text-sm font-normal before:h-0 before:float-left before:pointer-events-none',
        extensionOptions?.placeholder?.className ?? '',
      ),
      ...extensionOptions?.placeholder,
    }),
    Selection.configure({
      ...extensionOptions?.selection,
    }),
    Strike.configure({
      ...extensionOptions?.strike,
    }),
    Subscript.configure({
      ...extensionOptions?.subscript,
    }),
    Superscript.configure({
      ...extensionOptions?.superscript,
    }),
    Table.configure({
      resizable: true,
      HTMLAttributes: { class: 'tiptap-table min-w-full w-full my-0' },
      ...extensionOptions?.table,
    }),
    TableCell.configure({
      HTMLAttributes: { class: 'tiptap-table-cell' },
      ...extensionOptions?.tableCell,
    }),
    TableHeader.configure({
      HTMLAttributes: { class: 'tiptap-table-header' },
      ...extensionOptions?.tableHeader,
    }),
    TableRow.configure({
      HTMLAttributes: { class: 'tiptap-table-row' },
      ...extensionOptions?.tableRow,
    }),
    ChatCitation.configure({
      ...extensionOptions?.chatCitation,
    }),
    Text,
    TextAlign.configure({
      ...extensionOptions?.textAlign,
      types: ['heading', 'paragraph'],
    }),
    TextStyleExtended.configure({
      ...extensionOptions?.textStyle,
    }),
    Typography.configure({
      ...extensionOptions?.typography,
    }),
    Underline.configure({
      ...extensionOptions?.underline,
    }),
  ]

  // only remove comments if explicitly set to false
  if (extensionOptions === undefined || extensionOptions.comment !== false) {
    extensions.push(
      Comment.configure({
        ...extensionOptions?.comment,
      }),
    )
  } else {
    extensions.push(HiddenComment)
  }

  if (extensionOptions?.requireHeading) {
    extensions.push(RequireHeading)
  } else {
    extensions.push(
      Document.configure({
        ...extensionOptions?.document,
      }),
    )
  }

  if (
    extensionOptions?.indent === undefined ||
    extensionOptions.indent !== false
  ) {
    extensions.push(
      Indent.configure({
        ...extensionOptions?.indent,
      }),
    )
  }

  // only add mentions if explicitly defined
  if (
    extensionOptions !== undefined &&
    extensionOptions.mention !== undefined
  ) {
    extensions.push(
      Mention.configure({
        HTMLAttributes: {
          class:
            'font-normal text-blue-600 america:text-red-600 dark:text-blue-400',
        },
        renderText: ({ options, node }) => {
          return `${options.suggestion.char}${node.attrs.label ?? node.attrs.id}`
        },
        ...extensionOptions?.mention,
      }) as AnyExtension,
    )
  }

  return extensions
}

export const useExtensions = (options: UseExtensionsOptions = {}) => {
  const { collaborationConfig, extensionOptions, onStatus, onSynced } = options

  const { user } = useAuthInfoWithThrow()

  const suggestion = useSuggestions(extensionOptions?.mention)
  const setVersions = useSetVersions()

  const colorMapRef = useRef<Map<string, string>>(new Map())
  const color = randomcolor({ luminosity: 'dark' })

  const { doc, provider } = useMemo(() => {
    if (!collaborationConfig) {
      return { doc: null, provider: null }
    }
    const doc = new Y.Doc()
    const provider = new TiptapCollabProvider({
      document: doc,
      name: collaborationConfig.documentId,
      baseUrl: TIPTAP_URL,
      token: collaborationConfig.token,
      user: user.userId,
      onSynced: onSynced,
      onConnect: () => {
        onStatus?.(WebSocketStatus.Connected)
      },
      onDisconnect: () => {
        onStatus?.(WebSocketStatus.Disconnected)
      },
      onAuthenticationFailed: () => {
        onStatus?.(WebSocketStatus.Disconnected)
      },
    })
    return { doc, provider }
  }, [collaborationConfig?.documentId, collaborationConfig?.token, user.userId])

  const extensions = [
    ...getBaseExtensions({
      ...extensionOptions,
      mention: convertMentionArgsToTiptapMentionOptions(
        suggestion,
        extensionOptions?.mention,
      ),
    }),
    ...(collaborationConfig
      ? [
          Collaboration.configure({
            document: doc,
            ...extensionOptions?.collaboration,
          }),
          CollaborationCaret.configure({
            provider: provider,
            user: {
              id: user.userId,
              name: `${user.firstName} ${user.lastName}`,
              color: color,
            },
            render: (providedUser) => {
              const color =
                colorMapRef.current.get(providedUser.name) ??
                randomcolor({ luminosity: 'dark' })
              colorMapRef.current.set(providedUser.name, color)

              const cursor = document.createElement('span')

              cursor.classList.add('collaboration-cursor__caret')
              cursor.setAttribute('style', `border-color: ${color};`)

              const label = document.createElement('div')

              label.classList.add('collaboration-cursor__label')
              label.setAttribute('style', `background-color: ${color}`)
              label.insertBefore(
                document.createTextNode(providedUser.name),
                null,
              )
              cursor.insertBefore(label, null)

              return cursor
            },
          }),
          Snapshot.configure({
            ...extensionOptions?.collabHistory,
            provider: provider ?? undefined,
            onUpdate: (data) => {
              setVersions(data.versions)
              extensionOptions?.collabHistory?.onUpdate?.(data)
            },
          }),
        ]
      : [
          Gapcursor,
          UndoRedo.configure({
            ...extensionOptions?.history,
          }),
        ]),
  ]

  return {
    extensions,
    doc: collaborationConfig ? doc : null,
    provider,
  }
}

export const useRichTextEditor = (options: UseRichTextEditorOptions) => {
  const { extensions, doc, provider } = useExtensions(options)
  const editor = useEditor({
    extensions: [...extensions, ...(options.extensions ?? [])],
    immediatelyRender: false,
    ...options,
  })

  return { editor, doc, provider }
}

export const useRichTextAreaHandlers = ({
  ref,
  editor,
  children,
  enterVariant = 'submit',
  disabled,
  editable,
  greedyFocus,
  setIsFocused,
  onChange,
  onSubmit,
  onKeyDown,
}: UseRichTextAreaHandlersProps) => {
  const idRef = useRef<string>(uuidv4())
  const containerRef = useRef<HTMLDivElement>(null)

  const { shiftEnter, filteredChildren } = useMemo(() => {
    let shiftEnter: React.ReactNode | null = null
    const filteredChildren: React.ReactNode[] = []

    React.Children.forEach(children, (child) => {
      if (isShiftEnterMessage(child)) {
        shiftEnter = child
      } else {
        filteredChildren.push(child)
      }
    })

    return {
      shiftEnter: shiftEnter as React.ReactNode | null,
      filteredChildren,
    }
  }, [children])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      const isCtrlOrCommand = event.metaKey || event.ctrlKey
      const isNewLine = event.shiftKey || enterVariant === 'new-line'
      if (!isCtrlOrCommand && isNewLine) {
        return
      }
      if (onSubmit && (isCtrlOrCommand || enterVariant === 'submit')) {
        event.preventDefault()
        const result = onSubmit()

        const resetContent = () => {
          editor?.commands.setContent('')
        }

        if (result instanceof Promise) {
          // reset the content to empty after submitting
          result.then(resetContent)
        } else {
          // reset the content to empty after submitting
          resetContent()
        }
      }
    }
  }

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!editor?.isFocused && !disabled) {
      if (greedyFocus) {
        containerRef.current?.focus()
        editor?.chain().focus('end').run()
      }
      setIsFocused(true)
    }
  }

  const handleFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    if (disabled) return
    setIsFocused(true)
  }

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (
      !event.currentTarget.contains(event.relatedTarget as Node) &&
      !event.currentTarget.classList.contains(
        RICH_TEXT_AREA_NO_BLUR_CLASSNAME,
      ) &&
      !event.relatedTarget?.classList.contains(RICH_TEXT_AREA_NO_BLUR_CLASSNAME)
    ) {
      setIsFocused(false)
    }
  }

  useImperativeHandle(
    ref,
    () => ({
      ...containerRef.current!,
      editor,
      getHtml: () => {
        return editor?.getHTML() ?? ''
      },
      getJson: () => {
        return editor?.getJSON() ?? {}
      },
      getMarkdown: () => {
        return jsonToMarkdown(editor?.getJSON() ?? {})
      },
      setValue: (
        value: string,
        type: 'markdown' | 'json' | 'jsonString' = 'markdown',
      ) => {
        if (editor) {
          const currentSelection = editor.state.selection
          let content: string | null = null
          switch (type) {
            case 'markdown':
              content = markdownToHtml(value)
              break
            case 'json':
              if (typeof value === 'string') {
                content = getJsonContentFromString(value)
              } else {
                content = value
              }
              break
            case 'jsonString':
              content = getJsonContentFromString(value)
              break
          }
          if (content !== null) {
            editor
              .chain()
              .setContent(content)
              .setTextSelection(currentSelection)
              .run()
          }
          onChange?.(value)
        }
      },
      click: () => {
        containerRef.current?.focus()
      },
      focus: (position?: FocusPosition) => {
        containerRef.current?.focus()
        editor?.commands.focus(position)
      },
      blur: () => {
        containerRef.current?.blur()
        editor?.commands.blur()
      },
    }),
    [editor, onChange],
  )

  useEffect(() => {
    if (editor?.isFocused) {
      // only listen to focus, since the user may click on a header button
      // and we don't want to blur on that
      setIsFocused(true)
    }
  }, [editor?.isFocused])

  useEffect(() => {
    if (disabled || editable === false) {
      editor?.setEditable(false)
    } else {
      editor?.setEditable(true)
    }
  }, [disabled, editable, editor])

  return {
    id: idRef.current,
    editor,
    shiftEnter,
    filteredChildren,
    containerRef,
    handleClick,
    handleFocus,
    handleBlur,
    handleKeyDown,
  }
}

export const useRichTextArea = (
  props: Omit<UseRichTextAreaProps, 'collaborationConfig'>,
) => {
  const {
    placeholder,
    placeholderClassName,
    value,
    extensionsConfig,
    onKeyDown,
    ...rest
  } = props

  const content = useMemo(() => {
    if (
      value === undefined ||
      (typeof value === 'string' && value.trim().length === 0)
    ) {
      return ''
    }
    if (props.valueType === 'markdown') {
      return markdownToHtml(String(value ?? ''))
    } else if (props.valueType === 'json') {
      if (typeof value === 'string') {
        return getJsonContentFromString(value)
      }
      return getJsonContent(value)
    } else if (props.valueType === 'jsonString') {
      return getJsonContentFromString(String(value ?? ''))
    }
    return value
  }, [value])

  const handleUpdate = ({ editor }: { editor: TiptapEditor }) => {
    if (!props.onChange) {
      return
    }
    const json = editor.getJSON()
    if (!props.valueType || props.valueType === 'markdown') {
      const markdown = jsonToMarkdown(json)
      props.onChange(markdown)
    } else {
      props.onChange(JSON.stringify(json))
    }
  }

  const { editor, doc } = useRichTextEditor({
    extensionOptions: {
      placeholder: {
        placeholder: placeholder ?? '',
        className: placeholderClassName ?? '',
      },
      ...extensionsConfig,
    },
    editorProps: {
      handleDOMEvents: {
        keydown: (_, event) => {
          return onKeyDown?.(
            event as unknown as React.KeyboardEvent<HTMLDivElement>,
          )
        },
      },
    },
    content,
    onUpdate: handleUpdate,
  })

  const methods = useRichTextAreaHandlers({
    editor,
    ...rest,
  })

  useEffect(() => {
    if (doc) {
      return () => {
        doc?.destroy()
      }
    }
  }, [doc])

  return {
    ...methods,
    editor,
    doc,
  }
}

export const useCollaborativeRichTextArea = (options: UseRichTextAreaProps) => {
  // default to synced if there is no collaboration config
  const {
    placeholder,
    placeholderClassName,
    collaborationConfig,
    extensionsConfig,
    valueType,
    ...rest
  } = options

  const [isSynced, setIsSynced] = useState<boolean>(false)
  const [connectionStatus, setConnectionStatus] = useState<WebSocketStatus>(
    WebSocketStatus.Connecting,
  )

  const initialContent = useMemo(() => {
    if (
      options.value === undefined ||
      (typeof options.value === 'string' && options.value.trim().length === 0)
    ) {
      return ''
    }
    if (valueType === 'markdown') {
      return markdownToHtml(String(options.value ?? ''))
    } else if (options.valueType === 'json') {
      if (typeof options.value === 'string') {
        return getJsonContentFromString(options.value)
      }
      return getJsonContent(options.value)
    } else if (options.valueType === 'jsonString') {
      return getJsonContentFromString(String(options.value ?? ''))
    }
    return options.value
  }, [])

  const handleUpdate = ({ editor }: { editor: TiptapEditor }) => {
    if (!options.onChange) {
      return
    }
    const json = editor.getJSON()
    if (!valueType || valueType === 'markdown') {
      const markdown = jsonToMarkdown(json)
      options.onChange(markdown)
    } else {
      options.onChange(JSON.stringify(json))
    }
  }

  const { editor, doc, provider } = useRichTextEditor({
    extensionOptions: {
      placeholder: {
        placeholder: placeholder ?? '',
        className: placeholderClassName ?? '',
      },
      ...extensionsConfig,
    },
    collaborationConfig,
    onSynced: () => {
      setIsSynced(true)
    },
    onStatus: setConnectionStatus,
    onUpdate: handleUpdate,
    ...rest,
  })

  const methods = useRichTextAreaHandlers({
    ...options,
    editor,
  })

  useEffect(() => {
    if (provider && doc) {
      return () => {
        provider.destroy()
        doc?.destroy()
      }
    }
  }, [provider, doc])

  return {
    ...methods,
    isSynced,
    connectionStatus,
    provider,
    doc,
    initialContent,
  }
}

export const getEditor = (
  content: string | JSONContent,
  extensionOptions?: GetBaseExtensionsArgs,
) => {
  const extensions = [...getBaseExtensions(extensionOptions)]
  const editor = new Editor({ content, extensions })
  return editor
}

export const getCollaborativeEditor = (
  collaborationConfig: CollaborationConfig,
  extensionOptions?: GetBaseExtensionsArgs,
) => {
  const doc = new Y.Doc()

  const provider = new TiptapCollabProvider({
    document: doc,
    name: collaborationConfig.documentId,
    baseUrl: TIPTAP_URL,
    token: collaborationConfig.token,
  })

  const extensions = [
    ...getBaseExtensions(extensionOptions),
    Collaboration.configure({
      document: doc,
      ...extensionOptions?.collaboration,
    }),
  ]

  const editor = new Editor({ extensions })

  return { editor, doc, provider }
}

export const isValidJsonContent = (content: string) => {
  if (!content || content.length < 2) return false
  const firstChar = content[0]
  const lastChar = content[content.length - 1]
  return (
    (firstChar === '{' && lastChar === '}') ||
    (firstChar === '[' && lastChar === ']')
  )
}

/**
 * Content written in the collaborative editor lives in the yjs map's `default`
 * fragment, so it arrives as an object with no node type of its own.
 */
const unwrapYjsFragment = (content: JSONContent): JSONContent =>
  content && 'default' in content ? (content.default as JSONContent) : content

export const convertToJsonContent = (
  content: string | JSONContent,
): JSONContent => {
  if (typeof content === 'string') {
    if (isValidJsonContent(content)) {
      return unwrapYjsFragment(JSON.parse(content))
    }
    return {
      type: 'doc',
      content: [
        { type: 'paragraph', content: [{ type: 'text', text: content }] },
      ],
    }
  }
  return unwrapYjsFragment(content)
}

export const getHtmlFromJsonContent = (
  content: string | JSONContent,
  extensionOptions?: GetBaseExtensionsArgs,
) => {
  try {
    const extensions = [...getBaseExtensions(extensionOptions)]
    return generateHTML(convertToJsonContent(content), extensions)
  } catch {
    return typeof content === 'string' ? content : ''
  }
}

export const renderAsComponent = (
  content: string | JSONContent,
  extensionOptions?: GetBaseExtensionsArgs,
) => {
  const extensions = [...getBaseExtensions(extensionOptions)]
  return renderToReactElement({
    content: convertToJsonContent(content),
    extensions,
  })
}
